import React, { useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";

export interface OtpVerificationModalProps {
  open: boolean;
  onClose: () => void;
  timerSeconds: number;
  onResend: () => void;
  children: React.ReactNode;
  /** When provided, Verify button calls this instead of submitting a form (use when modal is portaled). */
  onVerifyClick?: () => void;
  title?: string;
  subtitle?: string;
  resendLabel?: string;
  verifyButtonText?: string;
  /** Error message to show below the OTP field inside the modal (e.g. "Invalid OTP. 2 attempts remaining"). */
  errorMessage?: string | null;
  /**
   * Tazty partner sign-in: neutral overlay + charcoal CTA (avoids theme primary blue on green partner UI).
   * Omit or false → Valar / default theme (primary blues, theme backdrop).
   */
  partnerChrome?: boolean;
  /**
   * When true (default), the first OTP digit is focused after the dialog opens.
   * Uses `disableAutoFocus` on the underlying Modal so focus does not land on the back button first.
   */
  autoFocusOtp?: boolean;
}

const OTP_VERIFICATION_DIALOG_PAPER_ID = "otp-verification-dialog-paper";

const formatTimer = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const OtpVerificationModal: React.FC<OtpVerificationModalProps> = ({
  open,
  onClose,
  timerSeconds,
  onResend,
  children,
  onVerifyClick,
  title = "Please Enter OTP",
  subtitle = "We have sent you one time OTP to your mobile number",
  resendLabel = "Didn't receive the OTP?",
  verifyButtonText = "Verify & Continue",
  errorMessage,
  partnerChrome = false,
  autoFocusOtp = true,
}) => {
  const theme = useTheme();
  const canResend = timerSeconds <= 0;
  const palette = theme.palette as typeof theme.palette & { grey?: Record<number, string> };
  const grey = palette.grey || { 300: "#e0e0e0", 400: "#bdbdbd", 500: "#9e9e9e", 600: "#757575" };

  const overlaySx = partnerChrome
    ? {
        zIndex: 10000,
        backgroundColor: "rgba(0, 0, 0, 0.58)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }
    : { zIndex: 10000 };

  const iconCircleBg = partnerChrome ? "rgba(0, 0, 0, 0.06)" : alpha(palette.primary.main, 0.1);
  const iconColor = partnerChrome ? grey[700] ?? "#616161" : ("primary.main" as const);
  const titleColor = partnerChrome
    ? palette.text.primary
    : (palette.primary.dark ?? palette.primary.main);
  const accentColor = partnerChrome ? palette.text.primary : ("primary.main" as const);

  const handleTransitionEntered = useCallback(() => {
    if (!autoFocusOtp) return;
    requestAnimationFrame(() => {
      const paper = document.getElementById(OTP_VERIFICATION_DIALOG_PAPER_ID);
      const firstDigit = paper?.querySelector<HTMLInputElement>('input[inputmode="numeric"]');
      firstDigit?.focus({ preventScroll: true });
    });
  }, [autoFocusOtp]);

  return (
    <Dialog
      open={open}
      disableAutoFocus={autoFocusOtp}
      onClose={(_, reason) => {
        // Keep OTP dialog open on outside clicks; only explicit actions (back button / verify)
        // or Escape should close it.
        if (reason === "backdropClick") return;
        if (reason === "escapeKeyDown") onClose();
      }}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntered: handleTransitionEntered }}
      sx={{
        zIndex: 10000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      slotProps={{
        backdrop: {
          sx: overlaySx,
        },
      }}
      PaperProps={{
        id: OTP_VERIFICATION_DIALOG_PAPER_ID,
        sx: {
          zIndex: 10001,
          minWidth: 380,
          minHeight: 420,
          maxWidth: 440,
          borderRadius: 3,
          p: 0,
          overflow: "visible",
          backgroundColor: palette.background?.paper ?? "#fff",
          border: "1px solid",
          borderColor: palette.divider,
          boxShadow: "0px 11px 15px -7px rgba(0,0,0,0.08), 0px 24px 38px 3px rgba(0,0,0,0.06)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, "&:first-of-type": { pt: 0 } }}>
        <Box sx={{ px: 4, pt: 3.5, pb: 4, minHeight: 420 }}>
          {/* Back button */}
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              left: 16,
              top: 16,
              width: 40,
              height: 40,
              bgcolor: grey[100] ?? "#f5f5f5",
              color: palette.text.primary,
              borderRadius: 2,
              "&:hover": {
                bgcolor: grey[200] ?? "#eeeeee",
              },
            }}
            size="medium"
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2.5,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: iconCircleBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SmsOutlinedIcon
                sx={{ fontSize: 44, color: iconColor }}
                aria-hidden
              />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "1.375rem",
              lineHeight: 1.3,
              color: titleColor,
              mb: 1,
            }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "0.9375rem",
              lineHeight: 1.5,
              color: palette.text.secondary ?? grey[600],
              mb: 2.5,
              px: 0.5,
            }}
          >
            {subtitle}
          </Typography>

          {/* Timer */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              mb: 2.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.125rem",
                fontWeight: 700,
                color: accentColor,
                letterSpacing: "0.02em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatTimer(timerSeconds)}
            </Typography>
          </Box>

          {/* OTP input (children) */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: errorMessage ? 1 : 2.5 }}>
            {children}
          </Box>

          {/* OTP error message inside modal */}
          {errorMessage && (
            <Typography
              sx={{
                color: "error.main",
                fontSize: "0.8125rem",
                fontWeight: 500,
                textAlign: "center",
                mb: 2.5,
              }}
            >
              {errorMessage}
            </Typography>
          )}

          {/* Resend */}
          <Box
            sx={{
              textAlign: "center",
              mb: 3,
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: "0.9375rem", color: palette.text.secondary ?? grey[600] }}
            >
              {resendLabel}{" "}
            </Typography>
            {canResend ? (
              <Typography
                component="button"
                type="button"
                onClick={onResend}
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: accentColor,
                  bgcolor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  p: 0,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                RESEND
              </Typography>
            ) : (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: grey[500] ?? "#9e9e9e",
                }}
              >
                RESEND
              </Typography>
            )}
          </Box>

          {/* Verify button */}
          <Button
            type={onVerifyClick ? "button" : "submit"}
            fullWidth
            variant="contained"
            color={partnerChrome ? undefined : "primary"}
            onClick={onVerifyClick}
            sx={
              partnerChrome
                ? {
                    borderRadius: 2,
                    py: 1.75,
                    fontSize: "1.0625rem",
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: "0.02em",
                    boxShadow: 1,
                    color: "#fff",
                    background: "linear-gradient(180deg, #1a1f2e 0%, #0b0d12 100%)",
                    "&:hover": {
                      boxShadow: 2,
                      background: "linear-gradient(180deg, #252b3d 0%, #12151c 100%)",
                    },
                  }
                : {
                    borderRadius: 2,
                    py: 1.75,
                    fontSize: "1.0625rem",
                    fontWeight: 600,
                    textTransform: "none",
                    letterSpacing: "0.02em",
                    boxShadow: 1,
                    "&:hover": { boxShadow: 2 },
                  }
            }
          >
            {verifyButtonText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OtpVerificationModal;
